import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "qgl_cart_v1";

// ─── Pricing rules ──────────────────────────────────────────────
// All values in CAD.
export const SHIPPING_FLAT_RATE = 19.99;
export const FREE_SHIPPING_THRESHOLD = 150;
export const DISCOUNT_THRESHOLD = 250;
export const DISCOUNT_RATE = 0.10; // 10% off when subtotal >= $250

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const hydratedRef = useRef(false);
    const firstSaveDoneRef = useRef(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) setItems(JSON.parse(raw));
        } catch (e) {
            console.warn("cart restore failed", e);
        }
        hydratedRef.current = true;
    }, []);

    useEffect(() => {
        if (!hydratedRef.current) return;
        if (!firstSaveDoneRef.current) {
            firstSaveDoneRef.current = true;
            return;
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = useCallback((product) => {
        setItems((prev) => {
            const existing = prev.find((p) => p.id === product.id);
            if (existing) {
                return prev.map((p) => (p.id === product.id ? { ...p, quantity: p.quantity + 1 } : p));
            }
            return [...prev, { ...product, quantity: 1 }];
        });
        setIsOpen(true);
    }, []);

    const removeItem = useCallback((id) => {
        setItems((prev) => prev.filter((p) => p.id !== id));
    }, []);

    const updateQty = useCallback((id, qty) => {
        setItems((prev) =>
            prev
                .map((p) => (p.id === id ? { ...p, quantity: Math.max(0, qty) } : p))
                .filter((p) => p.quantity > 0)
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    // ─── Derived totals (re-computed live whenever items change) ─────
    const pricing = useMemo(() => {
        const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
        const totalCount = items.reduce((s, i) => s + i.quantity, 0);

        const discountApplied = subtotal >= DISCOUNT_THRESHOLD;
        const discountAmount = discountApplied
            ? Math.round(subtotal * DISCOUNT_RATE * 100) / 100
            : 0;

        const subtotalAfterDiscount = Math.max(0, subtotal - discountAmount);

        const freeShippingApplied = subtotal >= FREE_SHIPPING_THRESHOLD;
        const shippingAmount =
            items.length === 0 ? 0 : freeShippingApplied ? 0 : SHIPPING_FLAT_RATE;

        const grandTotal = Math.round((subtotalAfterDiscount + shippingAmount) * 100) / 100;
        const amountToDiscount = Math.max(0, DISCOUNT_THRESHOLD - subtotal);
        const amountToFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);

        return {
            subtotal: Math.round(subtotal * 100) / 100,
            totalCount,
            discountApplied,
            discountAmount,
            subtotalAfterDiscount,
            freeShippingApplied,
            shippingAmount,
            grandTotal,
            amountToDiscount,
            amountToFreeShipping,
        };
    }, [items]);

    return (
        <CartContext.Provider
            value={{
                items,
                isOpen,
                setIsOpen,
                addItem,
                removeItem,
                updateQty,
                clear,
                // Back-compat aliases (Checkout still reads totalPrice/totalCount)
                totalCount: pricing.totalCount,
                totalPrice: pricing.subtotal,
                // New pricing fields
                ...pricing,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
